import React from 'react';
import { getIdTokenResult, signInWithCustomToken, signOut } from 'firebase/auth';
import { auth as firebaseAuth } from '../firebase';
import { getFirebaseCustomToken, getSession, loginUrl, logout as logoutRequest } from './client';
import { trackEvent } from '../lib/analytics';
import { isCommitteeAdmin } from '../lib/admin';
import type { AuthSessionUser } from './types';

interface AuthContextValue {
  loading: boolean;
  user: AuthSessionUser | null;
  authError: string | null;
  refresh: () => Promise<void>;
  login: () => void;
  logout: () => Promise<void>;
}

const AuthContext = React.createContext<AuthContextValue | null>(null);
const SESSION_CACHE_KEY = 'wa.auth.cached_user.v1';
const LAST_VERIFY_AT_KEY = 'wa.auth.last_verify_at.v1';
const DEFAULT_RECHECK_SECONDS = 60 * 60 * 24;
const POST_LOGOUT_URL = 'https://sytelineusers.com/';

function recheckIntervalMs() {
  const raw = Number(import.meta.env.VITE_AUTH_RECHECK_SECONDS || DEFAULT_RECHECK_SECONDS);
  const seconds = Number.isFinite(raw) && raw >= 300 ? raw : DEFAULT_RECHECK_SECONDS;
  return seconds * 1000;
}

function readCachedUser(): AuthSessionUser | null {
  try {
    const raw = window.localStorage.getItem(SESSION_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AuthSessionUser;
    if (parsed?.sessionExpiresAt && Number(parsed.sessionExpiresAt) <= Date.now()) {
      window.localStorage.removeItem(SESSION_CACHE_KEY);
      return null;
    }
    return parsed?.sub ? parsed : null;
  } catch {
    return null;
  }
}

function writeCachedUser(user: AuthSessionUser | null) {
  try {
    if (!user) {
      window.localStorage.removeItem(SESSION_CACHE_KEY);
      return;
    }
    window.localStorage.setItem(SESSION_CACHE_KEY, JSON.stringify(user));
  } catch {
    // Ignore storage failures (private mode/quotas).
  }
}

function readLastVerifyAt(): number {
  try {
    const raw = window.localStorage.getItem(LAST_VERIFY_AT_KEY);
    const parsed = raw ? Number(raw) : 0;
    return Number.isFinite(parsed) ? parsed : 0;
  } catch {
    return 0;
  }
}

function writeLastVerifyAt(ts: number) {
  try {
    window.localStorage.setItem(LAST_VERIFY_AT_KEY, String(ts));
  } catch {
    // Ignore storage failures.
  }
}

function clearVerifyState() {
  try {
    window.localStorage.removeItem(SESSION_CACHE_KEY);
    window.localStorage.removeItem(LAST_VERIFY_AT_KEY);
  } catch {
    // Ignore storage failures.
  }
}

function currentReturnTo() {
  const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (window.location.pathname === '/' || window.location.pathname === '/discussion') return '/discussions';
  return current;
}

export function AuthProvider(props: { children: React.ReactNode }) {
  const cachedUserRef = React.useRef<AuthSessionUser | null>(readCachedUser());
  const recheckMsRef = React.useRef<number>(recheckIntervalMs());
  const previousUserSubRef = React.useRef<string>(cachedUserRef.current?.sub || '');
  const [user, setUser] = React.useState<AuthSessionUser | null>(cachedUserRef.current);
  const [loading, setLoading] = React.useState(() => {
    const hasCached = Boolean(cachedUserRef.current?.sub);
    const isDue = Date.now() - readLastVerifyAt() >= recheckMsRef.current;
    return !hasCached || isDue;
  });
  const [authError, setAuthError] = React.useState<string | null>(null);

  const syncSession = React.useCallback(async (opts?: { foreground?: boolean }) => {
    const foreground = Boolean(opts?.foreground);
    if (foreground) {
      setLoading(true);
      setAuthError(null);
    }
    try {
      const next = await getSession();
      writeLastVerifyAt(Date.now());
      if (!next?.sub) {
        if (firebaseAuth.currentUser) {
          await signOut(firebaseAuth).catch(() => undefined);
        }
        setUser(null);
        clearVerifyState();
        setAuthError(null);
        return;
      }
      const currentFirebaseUser = firebaseAuth.currentUser;
      let shouldRefreshFirebaseToken = currentFirebaseUser?.uid !== next.sub;
      if (!shouldRefreshFirebaseToken && currentFirebaseUser) {
        try {
          const tokenResult = await getIdTokenResult(currentFirebaseUser);
          shouldRefreshFirebaseToken =
            (tokenResult.claims.committeeAdmin === true) !== isCommitteeAdmin(next) ||
            (tokenResult.claims.presenterSupport === true) !== (next.presenterSupport === true) ||
            String(tokenResult.claims.presenterConferenceId || '') !== String(next.presenterConferenceId || '');
        } catch {
          shouldRefreshFirebaseToken = true;
        }
      }

      if (shouldRefreshFirebaseToken) {
        const customToken = await getFirebaseCustomToken();
        await signInWithCustomToken(firebaseAuth, customToken);
      }
      setUser(next);
      writeCachedUser(next);
      setAuthError(null);
    } catch (err: any) {
      if (foreground) {
        if (firebaseAuth.currentUser) {
          await signOut(firebaseAuth).catch(() => undefined);
        }
        setUser(null);
        clearVerifyState();
      }
      const message = String(err?.message || '');
      if (message.includes('Firebase token request failed')) {
        setAuthError('firebase_token_unavailable');
      } else if (message.includes('Session request failed')) {
        setAuthError('session_unavailable');
      }
    } finally {
      if (foreground) setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    const hasCached = Boolean(cachedUserRef.current?.sub);
    const isDue = Date.now() - readLastVerifyAt() >= recheckMsRef.current;
    if (!hasCached || isDue) {
      void syncSession({ foreground: true });
      return;
    }
    setLoading(false);
  }, [syncSession]);

  React.useEffect(() => {
    const interval = window.setInterval(() => {
      void syncSession({ foreground: false });
    }, recheckMsRef.current);
    return () => window.clearInterval(interval);
  }, [syncSession]);

  React.useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      const isDue = Date.now() - readLastVerifyAt() >= recheckMsRef.current;
      if (isDue) void syncSession({ foreground: false });
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [syncSession]);

  React.useEffect(() => {
    const expiresAt = Number(user?.sessionExpiresAt || 0);
    if (!Number.isFinite(expiresAt) || expiresAt <= 0) return;

    const delay = expiresAt - Date.now();
    if (delay <= 0) {
      void syncSession({ foreground: true });
      return;
    }

    const timeout = window.setTimeout(
      () => void syncSession({ foreground: true }),
      Math.min(delay + 1000, 2_147_483_647)
    );
    return () => window.clearTimeout(timeout);
  }, [syncSession, user?.sessionExpiresAt]);

  React.useEffect(() => {
    const nextSub = user?.sub || '';
    if (!nextSub) {
      previousUserSubRef.current = '';
      return;
    }
    if (previousUserSubRef.current === nextSub) return;
    previousUserSubRef.current = nextSub;
    void trackEvent('login', { method: user?.presenterSupport ? 'presenter_support' : 'wild_apricot' });
  }, [user?.presenterSupport, user?.sub]);

  const refresh = React.useCallback(async () => {
    await syncSession({ foreground: true });
  }, [syncSession]);

  const login = React.useCallback(() => {
    window.location.assign(loginUrl(currentReturnTo()));
  }, []);

  const logout = React.useCallback(async () => {
    try {
      await logoutRequest();
    } finally {
      await signOut(firebaseAuth).catch(() => undefined);
      clearVerifyState();
      setAuthError(null);
      setUser(null);
      window.location.assign(POST_LOGOUT_URL);
    }
  }, []);

  const value = React.useMemo<AuthContextValue>(
    () => ({
      loading,
      user,
      authError,
      refresh,
      login,
      logout
    }),
    [loading, user, authError, refresh, login, logout]
  );

  return <AuthContext.Provider value={value}>{props.children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = React.useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
